import type { ProjectDetail } from "@esi/shared";
import { MessagesSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConversationPage } from "../../conversation/queries.ts";
import { formatAgo } from "../../i18n/format.ts";
import { conversationPath, projectHomePath } from "../../router.tsx";
import { HomeEmptyLine, HomeRow, HomeRows, HomeSection, stateOf } from "./HomeSection.tsx";

/** As many as the sidebar lists under an expanded project, so both share one request. */
export const RECENT_CONVERSATIONS = 5;

/**
 * Recent conversations (spec `project-home` "Recent conversations"): the viewer's five latest
 * conversations in the project (created by or shared with them, as their Conversations tab lists
 * them, never through break-glass), newest activity first, to pick one back up; "Show all" opens
 * the Conversations tab. The same request as the sidebar's expanded project, so one fetch serves
 * both and a new turn refreshes both.
 */
export function RecentConversations({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const recent = useConversationPage({ projectId: project.id, limit: RECENT_CONVERSATIONS });
  const items = recent.data?.items ?? [];
  return (
    <HomeSection
      id="recent-conversations"
      title={t("projectOverview.recent.title")}
      link={{
        href: projectHomePath(project.id, "conversations"),
        label: t("nav.showAll"),
      }}
      state={stateOf([recent], 3)}
    >
      {items.length === 0 ? (
        <HomeEmptyLine>{t("projectOverview.recent.empty")}</HomeEmptyLine>
      ) : (
        <HomeRows>
          {items.map((c) => (
            <HomeRow
              key={c.id}
              icon={MessagesSquare}
              href={conversationPath(c)}
              title={c.title ?? t("conversation.untitled")}
              at={c.updatedAt}
              atLabel={formatAgo(c.updatedAt)}
            />
          ))}
        </HomeRows>
      )}
    </HomeSection>
  );
}
